/* global Word */

import OfficeSettings from "@/constants/office";
import { splitSearch } from "../desktop/search";
import { singleSearch } from "../web/search";
import { DocumentParagraph, OfficeEnvironment, SearchFnParams } from "@/types/document";
import { getDocumentParagraphs } from "./text";
import { calculateParagraphSimilarity, generateWordNGrams } from "../utils/text-operators";
import { FUZZY_MATCH_CONFIDENCE_THRESHOLD, PARAGRAPH_LOCATION_INTERVAL } from "@/constants";
import Fuse, { IFuseOptions } from "fuse.js";
import log from "../../../utils/logger";
/**
 * Loads the isNullObject property of a Word.Range and returns the range if it is valid, otherwise null.
 *
 * @param range - The Word.Range to process.
 * @returns The range if it is not a null object, otherwise null.
 */
const processRangeResult = async (range: Word.Range): Promise<Word.Range | null> => {
  if (!range) {
    return null;
  }
  try {
    log.debug("loading isNullObject", "processRangeResult", { range });
    range.load("isNullObject");
    await range.context.sync();
    log.debug("isNullObject result", "processRangeResult", { isNullObject: range.isNullObject });
    return range.isNullObject ? null : range;
  } catch (error) {
    log.error("error processing range result", "processRangeResult", { error });
    return null;
  }
};

/**
 * Attempts to find a paragraph in the document using multiple strategies:
 * 1. By unique ID (if available and on Office Online)
 * 2. By hash (if available)
 * 3. By approximate location and text similarity (if index is available)
 * Returns the Word.Range of the found paragraph, or null if not found.
 *
 * @param params - SearchFnParams containing context and paragraph information.
 * @returns The Word.Range of the found paragraph, or null.
 */
export const paragraphSearch = async (params: SearchFnParams): Promise<Word.Range | null> => {
  const { paragraph: inputParagraph } = params;

  // Step 1: search by id
  // If on web we can search by the id on the document
  if (OfficeSettings.environment === OfficeEnvironment.Online) {
    const paragraphId = inputParagraph?.id || params.paragraphId;
    if (paragraphId) {
      log.debug("searching by id", "paragraphSearch", { paragraphId });
      const paragraph = await getParagraphById(params.context, paragraphId);
      if (paragraph) {
        log.debug("found paragraph by id", "paragraphSearch", { paragraphId, paragraph });
        return processRangeResult(paragraph.getRange());
      } else {
        log.debug("paragraph not found by id", "paragraphSearch", { paragraphId });
      }
    }
  }
  // Step 2: search by hash
  if (inputParagraph?.hash) {
    log.debug("searching by hash", "paragraphSearch", { hash: inputParagraph.hash });
    const paragraph = await findParagraphByHash(params.context, inputParagraph.hash);
    if (paragraph) {
      log.debug("found paragraph by hash", "paragraphSearch", { hash: inputParagraph.hash, paragraph });
      return processRangeResult(paragraph.getRange());
    } else {
      log.debug("paragraph not found by hash", "paragraphSearch", { hash: inputParagraph.hash });
    }
  }

  // Step 3: try to match it by location if available
  if (typeof inputParagraph?._index === "number") {
    log.debug("searching by location", "paragraphSearch", { _index: inputParagraph._index });
    const paragraph = (await getParagraphByLocation(
      params.context,
      inputParagraph._index,
      inputParagraph
    )) as Word.Paragraph;
    if (paragraph) {
      log.debug("found paragraph by location", "paragraphSearch", { _index: inputParagraph._index, paragraph });
      return processRangeResult(paragraph.getRange());
    } else {
      log.debug("paragraph not found by location", "paragraphSearch", { _index: inputParagraph._index });
    }
  }

  log.debug("no paragraph found", "paragraphSearch", { inputParagraph });
  return null;
};

/**
 * Main search function that attempts to find a text range in the document.
 * 1. Attempts to find the paragraph using paragraphSearch.
 * 2. If text search is enabled, searches for the text within the found paragraph.
 * 3. If fuzzy search is enabled and no exact match is found, performs fuzzy matching using Fuse.js.
 * 4. Falls back to returning the paragraph range if no text match is found and fallback is enabled.
 *
 * @param params - SearchFnParams containing search parameters.
 * @returns An object with the found range and metadata about the search.
 */
export const search = async (
  params: SearchFnParams
): Promise<{
  range: Word.Range | null;
  metadata: { fuzzy: boolean; paragraph: boolean; paragraphRange?: Word.Range };
} | null> => {
  const shouldSearchText = params.textSearch ?? true;
  const fallbackToParagraphWhenTextNotFound = params.fallbackToParagraphWhenTextNotFound ?? true;
  const fuzzySearch = params.fuzzySearch ?? false;

  log.debug("params", "search", { params });

  // Step 1: we search for the paragraph by id, hash or location
  const paragraphRange = await paragraphSearch(params);

  if (!shouldSearchText) {
    if (!paragraphRange) {
      log.debug("skipping text search, no paragraphRange found", "search");
      return null;
    }
    log.debug("skipping text search, returning paragraphRange", "search", { paragraphRange });
    return {
      range: await processRangeResult(paragraphRange),
      metadata: { fuzzy: false, paragraph: true },
    };
  }

  // Step 3: we search for the text in the paragraph
  let finalRange: Word.Range | null = null;
  // On desktop there is a limitation of 255 chars, so we have to split, and perform multiple searches
  // It's not as performant so we fork the behavior here
  log.debug("searching for the text", "search", { shouldSearchText, paragraphRange });

  if (OfficeSettings.environment === OfficeEnvironment.Desktop) {
    log.debug("using splitSearch for Desktop", "search", { params, paragraphRange });
    finalRange = await splitSearch(params, paragraphRange);
  } else {
    log.debug("using singleSearch for Online", "search", { params, paragraphRange });
    finalRange = await singleSearch(params, paragraphRange);
  }

  finalRange = await processRangeResult(finalRange);

  // Track if a fuzzy match was found
  let fuzzyMatched = false;

  const text = params.text || params.paragraph?.text;
  // If fuzzy search is enabled and we have a paragraph range
  if (fuzzySearch && paragraphRange && text && (!finalRange || finalRange?.isNullObject)) {
    log.debug("fuzzySearch enabled", "search", { fuzzySearch, paragraphRange, text });

    // Load the text of the paragraph range for fuzzy searching
    log.debug("loading paragraphRange text for fuzzy search", "search", { paragraphRange });
    paragraphRange.load("text");
    await paragraphRange.context.sync();

    // Set up Fuse.js options for fuzzy matching
    const fuseOptions: IFuseOptions<string> = {
      includeScore: true,
      threshold: 0.9, // @TODO: review this value
    };

    // Generate n-grams from the paragraph text for better fuzzy matching
    log.debug("generating n-grams for fuzzy search", "search", {
      paragraphText: paragraphRange.text,
      ngramLength: text.split(/\s+/).length,
    });
    const ngrams = generateWordNGrams(paragraphRange.text, text.split(/\s+/).length);
    log.debug("n-grams generated", "search", { ngrams });

    const fuse = new Fuse(ngrams, fuseOptions);

    // Perform the fuzzy search, limiting to the best result
    log.debug("performing fuzzy search", "search", { searchText: text });
    const fuzzyResults = fuse.search(text, { limit: 1 });
    log.debug("fuzzy search results", "search", { fuzzyResults });

    // If we found a fuzzy match, update the search parameters and try searching again
    if (fuzzyResults.length) {
      fuzzyMatched = true;
      log.debug("fuzzy match found", "search", { fuzzyResult: fuzzyResults[0] });

      // Get the first paragraph safely
      const firstParagraph = paragraphRange.paragraphs?.getFirstOrNullObject();
      firstParagraph?.load("isNullObject");
      await firstParagraph?.context.sync();
      if (!firstParagraph || firstParagraph?.isNullObject) {
        log.debug("no first paragraph found for fuzzy search", "search");
        return {
          range: null,
          metadata: { fuzzy: false, paragraph: false, paragraphRange },
        };
      }

      const localParams: SearchFnParams = {
        ...params,
        paragraph: { ...params.paragraph, _paragraph: firstParagraph },
        text: fuzzyResults[0].item,
      };
      if (OfficeSettings.environment === OfficeEnvironment.Desktop) {
        finalRange = await splitSearch(localParams, paragraphRange);
      } else {
        finalRange = await singleSearch(localParams, paragraphRange);
      }
    } else {
      log.debug("no fuzzy match found", "search");
    }
  }

  // If a fuzzy match was found, process the resulting range
  if (fuzzyMatched) {
    log.debug("processing fuzzy matched range", "search", { finalRange });
    finalRange = await processRangeResult(finalRange);
  }

  // If no valid range was found and fallback is enabled, return the paragraph range as a fallback
  // eslint-disable-next-line office-addins/test-for-null-using-isNullObject
  if ((!finalRange || finalRange?.isNullObject) && fallbackToParagraphWhenTextNotFound) {
    log.debug("no valid range found, falling back to paragraph range", "search", { paragraphRange });
    return {
      range: await processRangeResult(paragraphRange),
      metadata: { fuzzy: false, paragraph: true },
    };
  }

  log.debug("returning final result", "search", { finalRange, fuzzyMatched });
  return {
    range: finalRange,
    metadata: { fuzzy: fuzzyMatched, paragraph: false, paragraphRange },
  };
};

/**
 * Attempts to retrieve a paragraph from the Word document by its unique local ID.
 * Returns the paragraph if found, otherwise returns null.
 *
 * @param context - The Word.RequestContext for the Office.js API.
 * @param paragraphId - The unique local ID of the paragraph to retrieve.
 * @returns The matching Word.Paragraph if found, otherwise null.
 */
export const getParagraphById = async (
  context: Word.RequestContext,
  paragraphId: string
): Promise<Word.Paragraph | null> => {
  try {
    log.debug("loading paragraph by unique local id", "getParagraphById", { paragraphId });
    const paragraph = context.document.getParagraphByUniqueLocalId(paragraphId);
    paragraph.load("isNullObject");
    await context.sync();
    log.debug("paragraph loaded", "getParagraphById", { isNullObject: paragraph.isNullObject, paragraph });
    return paragraph.isNullObject ? null : paragraph;
  } catch (error) {
    log.error("no paragraph found with unique local id", "getParagraphById", { paragraphId });
    return null;
  }
};

const findParagraphByHash = async (context: Word.RequestContext, hash: string): Promise<Word.Paragraph | null> => {
  try {
    log.debug("getting document paragraphs", "findParagraphByHash", { hash });
    const paragraphs = await getDocumentParagraphs(context);

    for (const paragraph of paragraphs) {
      // log.debug("checking paragraph hash", "findParagraphByHash", { paragraphHash: paragraph.hash, targetHash: hash });
      if (paragraph.hash === hash) {
        log.debug("found paragraph by hash", "findParagraphByHash", { paragraph });
        return paragraph._paragraph;
      }
    }
    log.debug("no paragraph found with hash", "findParagraphByHash", { hash });
    return null;
  } catch (error) {
    log.error("error finding paragraph by hash", "findParagraphByHash", { hash, error });
    return null;
  }
};

/**
 * Attempts to find a paragraph in the document by its approximate location (index) and text similarity.
 * This is used as a fallback when the paragraph's ID or hash is unavailable or unreliable.
 * The function searches for paragraphs within a small index range of the target location and compares their text similarity.
 *
 * @param context - The Word.RequestContext for the Office.js API.
 * @param location - The target paragraph index to search for.
 * @param inputParagraph - The reference DocumentParagraph containing the expected text and index.
 * @param getDocumentParagraph - If true, returns the DocumentParagraph; otherwise, returns the underlying Word.Paragraph.
 * @returns The matching Word.Paragraph or DocumentParagraph if found, otherwise null.
 */
const getParagraphByLocation = async (
  context: Word.RequestContext,
  location: number,
  inputParagraph: DocumentParagraph,
  getDocumentParagraph: boolean | undefined = false
): Promise<Word.Paragraph | DocumentParagraph | null> => {
  log.debug("getting document paragraphs", "getParagraphByLocation", { location, inputParagraph });

  // Validate inputs
  if (!inputParagraph?._currentText) {
    log.debug("missing required input paragraph text", "getParagraphByLocation", { inputParagraph });
    return null;
  }

  const paragraphs = await getDocumentParagraphs(context);

  let bestMatch: { similarity: number; paragraph: Word.Paragraph | DocumentParagraph | null } = {
    similarity: 0,
    paragraph: null,
  };
  for (const paragraph of paragraphs) {
    // Check if the paragraph is within 8 indexes of the target location (fuzzy index match)
    if (
      paragraph._index > location - PARAGRAPH_LOCATION_INTERVAL &&
      paragraph._index < location + PARAGRAPH_LOCATION_INTERVAL
    ) {
      // Validate paragraph text before similarity calculation
      if (!paragraph._currentText) {
        log.debug("skipping paragraph with missing text", "getParagraphByLocation", {
          paragraphIndex: paragraph._index,
        });
        continue;
      }

      // Score the paragraph's text similarity to ensure it's a good match
      const similarity = calculateParagraphSimilarity(paragraph._currentText, inputParagraph._currentText);
      log.debug("similarity score", "getParagraphByLocation", {
        paragraphIndex: paragraph._index,
        similarity,
        threshold: FUZZY_MATCH_CONFIDENCE_THRESHOLD,
      });
      if (similarity >= FUZZY_MATCH_CONFIDENCE_THRESHOLD) {
        log.debug("found paragraph by location and similarity", "getParagraphByLocation", { paragraph });
        // Keep track of the most similar paragraph
        if (similarity > bestMatch.similarity) {
          bestMatch = {
            similarity: similarity,
            paragraph: getDocumentParagraph ? paragraph : paragraph._paragraph,
          };
        }
      }
    }
  }

  return bestMatch.paragraph;
};

/**
 * Attempts to retrieve a DocumentParagraph from the document using multiple strategies:
 * 1. By unique ID (if available)
 * 2. By hash (if available)
 * 3. By approximate location and text similarity (if index is available)
 * Returns the DocumentParagraph if found, otherwise null.
 *
 * @param params - An object containing the context, paragraph, and paragraphId.
 * @returns The matching DocumentParagraph if found, otherwise null.
 */
export async function getParagraph(
  params: Pick<SearchFnParams, "context" | "paragraph" | "paragraphId">
): Promise<DocumentParagraph | null> {
  // input variables to use
  const { context, paragraphId: searchParagraphId, paragraph: searchParagraph } = params;
  const paragraphId = searchParagraph?.id ?? searchParagraphId;
  const paragraphs = await getDocumentParagraphs(context);

  // search by id
  let foundParagraph = paragraphs.find((p) => p.id === paragraphId);
  if (foundParagraph) {
    log.debug("found paragraph by id", "getParagraph", { paragraphId, paragraph: foundParagraph });
    return foundParagraph;
  }

  // search by hash
  const hash = searchParagraph?.hash;
  if (hash) {
    const paragraphByHash = paragraphs.find((p) => p.hash === hash);
    if (paragraphByHash) {
      log.debug("found paragraph by hash", "getParagraph", { hash, paragraph: paragraphByHash });
      return paragraphByHash;
    }
  }

  // search by index
  const index = searchParagraph?._index;
  if (typeof index === "number") {
    foundParagraph = (await getParagraphByLocation(context, index, searchParagraph, true)) as DocumentParagraph;
    if (foundParagraph) {
      log.debug("found paragraph by index", "getParagraph", { index, paragraph: foundParagraph });
      return foundParagraph;
    }
  }

  return null;
}
